import { Aviso } from "@/components/Aviso"
import { Marca } from "@/components/Marca"
import { Navegacao } from "@/components/Navegacao"
import { ApiError, type FalhaDoPortal } from "@/lib/api"
import { getToken } from "@/lib/session"
import { useParceiro } from "@/parceiros/contexto"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router"
import { acaoDaBusca, caminhoDoRepasse, fetchRepasse, referenciaDaBusca } from "./api"
import { BlocoInstituicoes } from "./components/BlocoInstituicoes"
import { ChamadaAlegacao } from "./components/ChamadaAlegacao"
import { EsqueletoRepasse } from "./components/EsqueletoRepasse"
import { NaoIdentificados } from "./components/NaoIdentificados"
import { NotaSegura } from "./components/NotaSegura"
import { SeletorCompetencia } from "./components/SeletorCompetencia"
import { TabelaContratos } from "./components/TabelaContratos"
import { Totais } from "./components/Totais"
import { rotuloDaGuia } from "./guia"
import type { Contrato } from "./schema"

const Repassados = ({ contratos }: { contratos: Contrato[] }) => {
    const repassados = contratos.filter((contrato) => contrato.situacao === "REPASSADO")
    if (!repassados.length) return null
    return (
        <div>
            <h3 className="mb-2 font-semibold">
                Repassado <span className="font-normal text-faint">({repassados.length})</span>
            </h3>
            <TabelaContratos contratos={repassados} />
        </div>
    )
}

export const RepassePage = () => {
    const parceiro = useParceiro()
    // O token é capturado antes do render (main.tsx) e vive na aba. Ele some ao fechar a aba, e não
    // existe numa aba nova — alguém copiou o endereço da barra e abriu em outro lugar. Sem essa
    // distinção o usuário levaria "seu link expirou" para um link que não expirou, e é o tipo de
    // mentira pequena que faz desistir ou abrir chamado.
    const semToken = !getToken()

    // Sem mês no link vale o aberto — é o caso de quem guardou o endereço e voltou depois.
    const [busca, setBusca] = useSearchParams()
    const caminho = caminhoDoRepasse(busca)
    const doLink = !caminho.endsWith("/active")
    const [acao, setAcao] = useState(() => acaoDaBusca(busca))

    useEffect(() => {
        if (!busca.has("acao")) return
        setBusca(
            (atual) => {
                const proxima = new URLSearchParams(atual)
                proxima.delete("acao")
                return proxima
            },
            { replace: true },
        )
    }, [busca, setBusca])

    // Os outros parâmetros ficam: a navegação entre telas leva a busca inteira junto.
    const abrir = (referencia?: string) =>
        setBusca((atual) => {
            const proxima = new URLSearchParams(atual)
            proxima.delete("r")
            proxima.delete("c")
            if (referencia) proxima.set("c", referencia)
            return proxima
        })

    const { data, isPlaceholderData, error, refetch } = useQuery({
        queryKey: ["repasse", caminho],
        queryFn: () => fetchRepasse(caminho),
        enabled: !semToken,
        placeholderData: keepPreviousData,
        // Link vencido e corpo fora do schema não melhoram na segunda tentativa; rede e erro do
        // servidor, às vezes sim.
        retry: (tentativas, erro) =>
            erro instanceof ApiError &&
            (erro.falha === "rede" || erro.falha === "servidor") &&
            tentativas < 2,
    })

    const pronto = Boolean(data) && !isPlaceholderData
    useEffect(() => {
        if (pronto) setAcao(null)
    }, [pronto])

    if (semToken) {
        return (
            <Aviso titulo="Abra esta página pelo link do e-mail" ocultarSuporte>
                <p>
                    O endereço sozinho não dá acesso: quem abre a página é o link que enviamos para
                    você. Procure o e-mail mais recente {parceiro.doParceiro} e abra a página pelo
                    link dele.
                </p>
                <p className="text-sm">
                    Se você colou este endereço em outra aba, volte para a aba original — o acesso
                    fica só nela, de propósito, para o link não vazar num print ou no histórico.
                </p>
            </Aviso>
        )
    }

    if (error) {
        const falha: FalhaDoPortal = error instanceof ApiError ? error.falha : "servidor"
        const tentarDeNovo = { rotulo: "Tentar de novo", onClick: () => void refetch() }

        if (falha === "recusado") {
            return (
                <Aviso
                    titulo="Este mês não aparece no portal"
                    comNavegacao
                    ocultarSuporte
                    acao={{ rotulo: "Ver o mês aberto", onClick: () => abrir() }}
                >
                    <p>
                        {(error instanceof ApiError && error.mensagem) ||
                            "O portal mostra o mês aberto e os seis anteriores."}
                    </p>
                </Aviso>
            )
        }

        if (falha === "link") {
            return (
                <Aviso titulo="Este link não vale mais" ocultarSuporte>
                    <p>
                        Por segurança o acesso expira depois de alguns dias, e o seu já passou da
                        validade.
                    </p>
                    <p>
                        Abra o <b>e-mail mais recente</b> {parceiro.doParceiro} e use o link de lá —
                        ele funciona. Se não encontrar, responda o e-mail e a gente manda um novo.
                    </p>
                </Aviso>
            )
        }

        if (falha === "vazio") {
            return (
                <Aviso titulo="Ainda não há repasse para mostrar" comNavegacao ocultarSuporte>
                    <p>
                        Não encontramos guia do consignado desta empresa para{" "}
                        {doLink ? "o mês do link" : "o mês atual"}. Ela aparece aqui quando a
                        competência abre para a sua folha.
                    </p>
                    <p className="text-sm">
                        Enquanto isso, a <b>Visão da folha</b> mostra como está a margem dos seus
                        colaboradores.
                    </p>
                </Aviso>
            )
        }

        if (falha === "rede") {
            return (
                <Aviso titulo="Não conseguimos conectar" acao={tentarDeNovo}>
                    <p>
                        A página não chegou até nós. Costuma ser a conexão de internet ou a rede da
                        empresa bloqueando o acesso.
                    </p>
                    <p className="text-sm">
                        Se estiver em rede corporativa, tentar do celular costuma resolver na hora.
                    </p>
                </Aviso>
            )
        }

        return (
            <Aviso titulo="Tivemos um problema por aqui" acao={tentarDeNovo}>
                <p>
                    O erro é nosso, não seu — nada do que você fez causou isso, e nada do que você
                    enviou se perdeu.
                </p>
                <p className="text-sm">
                    Já estamos sabendo. Tente de novo em alguns minutos; se insistir, a gente
                    resolve pelo e-mail.
                </p>
            </Aviso>
        )
    }

    // Na troca de mês a faixa da empresa fica, com a pílula já no mês escolhido, e só o repasse vira
    // esqueleto.
    const cabecalho = data && (
        <header className="flex flex-wrap items-center justify-between gap-4 bg-brand px-6 py-5 text-brand-ink">
            <div>
                <h1 className="text-xl font-black">{data.empregador}</h1>
                <p className="text-sm opacity-85">Repasse do consignado · {data.cnpj}</p>
            </div>
            <SeletorCompetencia
                selecionada={(isPlaceholderData && referenciaDaBusca(busca)) || data.referencia}
                rotulo={isPlaceholderData ? undefined : rotuloDaGuia(data)}
                onEscolher={abrir}
            />
        </header>
    )

    return (
        <main className="mx-auto max-w-4xl px-5 py-10">
            <Marca />
            <Navegacao />
            <NotaSegura className="mx-auto mb-4 w-fit">
                Ambiente seguro: só quem recebeu este link acessa esta página, que expira em alguns
                dias. Tratamos os dados conforme a LGPD.
            </NotaSegura>
            {!data || isPlaceholderData ? (
                <EsqueletoRepasse cabecalho={cabecalho} />
            ) : (
                <div className="overflow-hidden rounded-xl border border-line bg-card shadow-sm">
                    {cabecalho}

                    <ChamadaAlegacao
                        key={data.id}
                        repasse={data}
                        caminho={caminho}
                        abrir={acao === "alegar"}
                    />

                    <Totais repasse={data} />

                    <section className="border-t border-line px-6 py-6">
                        <h2 className="font-semibold">O que recebemos</h2>
                        <p className="mb-4 mt-1 max-w-[66ch] text-sm text-soft">
                            Cada linha é um contrato de um colaborador seu. O valor é o que entrou
                            pela guia do FGTS Digital, e a data é a do crédito.
                        </p>
                        <div className="flex flex-col gap-6">
                            <NaoIdentificados
                                repasse={data}
                                caminho={caminho}
                                focar={acao === "pagar"}
                            />
                            <Repassados contratos={data.contratos} />
                        </div>
                        <BlocoInstituicoes />
                    </section>
                </div>
            )}
        </main>
    )
}
